import { ProgressIndicator, Spinner } from "@fluentui/react";
import queryString from "query-string";
import { Suspense, lazy, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Navigate, Route, Routes, useParams } from "react-router-dom";

import { useGetProfile } from "../libs/api/react-query";

import AccountsRoutes from "./AccountsRoutes";
import ComplementaryDocumentsRoute from "./ComplementaryDocumentsRoute";
import ContractsRoutes from "./ContractsRoutes";
import MembershipAssociationRoute from "./MembershipAssociation";

import {
  PermissionsPosModule,
  PermissionsScope
} from "@/common/types/permissions.types";
import Layout from "@/components/Shared/Layouts/Layout";
import MainLayout from "@/components/Shared/Layouts/MainLayout";
import { useOrganization } from "@/hooks/useOrganization";
import { UrlFilterProvider } from "@/hooks/useUrlFilter";
import BusinessMaterial from "@/modules/BusinessMaterial";
import AccountInvoices from "@/modules/Invoices";
import News from "@/modules/News";
import Offers from "@/modules/Offers";
import Promotions from "@/modules/Promotions";
import ResultsPanel from "@/modules/ResultsPanel";
import UserProfile from "@/modules/User";
import { getProfileRequest } from "@/modules/User/actions/profile";

const Settings = lazy(() => import("@/modules/Settings"));

function LegacyTermRedirect(): JSX.Element {
  const { id } = useParams();

  return <Navigate to={`/membership-applications/${id}`} replace />;
}

export default function RoutesLayout(): JSX.Element {
  const redirect = queryString.parseUrl(window.location.href).query;

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getProfileRequest());
  }, [dispatch]);

  const {
    setOrganizations,
    organizations,
    openOrganizationSelection,
    organizationSelected
  } = useOrganization();

  const userProfileQuery = useGetProfile();
  const user = userProfileQuery.data?.data;

  useEffect(() => {
    if (user && !organizations) {
      setOrganizations(user?.organizations);
    }
  }, [user, setOrganizations, organizations]);

  useEffect(() => {
    const localOrganization = localStorage.getItem("organization");
    const loginLocations = ["/login", "/recover", "/set-password"];

    if (!localOrganization && !loginLocations.includes(location.pathname)) {
      if (organizations) openOrganizationSelection();

      if (!organizations && user?.organizations)
        openOrganizationSelection(user?.organizations);

      if (!organizationSelected && organizations) {
        openOrganizationSelection();
      }
    }
  }, [organizations, openOrganizationSelection, user, organizationSelected]);

  const progressElement = (
    <ProgressIndicator
      styles={{
        itemProgress: {
          padding: 0,
          position: "absolute",
          left: 0,
          right: 0,
          zIndex: 999
        }
      }}
    />
  );

  const progressSpinner = (
    <Spinner
      label="Carregando o Usuário..."
      styles={{
        root: {
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 999
        },
        label: { fontSize: "1rem", fontWeight: 600 }
      }}
    />
  );

  return (
    <Layout>
      <UrlFilterProvider>
        <Suspense fallback={progressElement}>
          {!user || !user.role?.permissions ? (
            progressSpinner
          ) : (
            <Routes>
              <Route
                path="/login"
                element={
                  <Navigate
                    to={
                      typeof redirect.origin === "string" && redirect.origin
                        ? redirect.origin
                        : "/"
                    }
                    replace
                  />
                }
              />
              {user.role.permissions?.[PermissionsScope.ADMIN] && (
                <Route
                  path="/settings/*"
                  element={<Settings permissions={user.role.permissions} />}
                />
              )}

              <Route
                path="*"
                element={
                  <MainLayout>
                    <Routes>
                      <Route path="/accounts/*" element={<AccountsRoutes />} />
                      <Route
                        path="/terms"
                        element={
                          <Navigate to="/membership-applications" replace />
                        }
                      />
                      <Route
                        path="/terms/:id"
                        element={<LegacyTermRedirect />}
                      />
                      <Route
                        path="/membership-applications/*"
                        element={<MembershipAssociationRoute />}
                      />
                      <Route
                        path="/complementary-documents/*"
                        element={<ComplementaryDocumentsRoute />}
                      />
                      <Route
                        path="/contracts/*"
                        element={<ContractsRoutes />}
                      />
                      <Route path="/user/profile" element={<UserProfile />} />
                      <Route path="/benefits" element={<Offers />} />
                      <Route path="/promotions" element={<Promotions />} />

                      {user.role.permissions?.[PermissionsScope.POS]?.[
                        PermissionsPosModule.INVOICES
                      ] && (
                        <Route
                          path="/member-invoices"
                          element={<AccountInvoices />}
                        />
                      )}

                      <Route path="/news" element={<News />} />
                      <Route
                        path="/business-material"
                        element={<BusinessMaterial />}
                      />
                      <Route path="/results-panel" element={<ResultsPanel />} />

                      <Route
                        path="*"
                        element={<Navigate to="/benefits" replace />}
                      />
                    </Routes>
                  </MainLayout>
                }
              />
            </Routes>
          )}
        </Suspense>
      </UrlFilterProvider>
    </Layout>
  );
}
